import { ALARM, CAT, ENSIGN, RITE_IDS, WAVES_PER_MAREA, bossAt, RITES, SABERES, type SaberId } from '@somnhold/content';
import { eraOf, offeringCost, riteFor, riteOpen, type CampaignCommand, type CampaignState, type SaberFind } from '@somnhold/sim';
import { getLang, listNames, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/**
 * The saberes on screen (docs/SABERES.md §6): the reveal of one just found, the bastion's list of the ones it knows,
 * and which ones this player hasn't seen yet. Never a total, never what is missing (rule 4).
 */

/** Who speaks a saber's line: Brann for traces and customs, Hilde for recipes, engines and rites. */
const SPEAKER: Record<string, 'brann' | 'hilde'> = { trace: 'brann', custom: 'brann', recipe: 'hilde', engine: 'hilde', rite: 'hilde' };
const KIND_ICON: Record<string, string> = { trace: ICONS.scroll, engine: ICONS.hammer, rite: ICONS.vigil, recipe: ICONS.hammer, custom: ICONS.heart };

const nameOf = (s: CampaignState, me: string, pid: string): string => (pid === me ? t('log.you') : s.players.find((p) => p.id === pid)?.displayName ?? '—');

const dateOf = (at: number): string => new Date(at).toLocaleDateString(getLang(), { day: 'numeric', month: 'long' });

/** «Lo trajeron Ana y Bo de la cripta, el 4 de noviembre». */
export const saberBy = (s: CampaignState, me: string, f: SaberFind): string =>
  // Brought by this player alone: «Lo trajiste…», never «Lo trajo vos…».
  f.by.length === 1 && f.by[0] === me
    ? t('saber.by.you', { from: t(`saber.from.${f.from}`), date: dateOf(f.at) })
    : t(f.by.length > 1 ? 'saber.by.many' : 'saber.by.one', { who: listNames(f.by.map((pid) => nameOf(s, me, pid))), from: t(`saber.from.${f.from}`), date: dateOf(f.at) });

/** One saber just found, shown off: the Vigil's cold white flame, what it is, its line of lore and what it opens. */
/** `label`: the «lost lore» header on the card (off where the modal already says it). */
export const saberReveal = (s: CampaignState, me: string, f: SaberFind, label = true): HTMLElement => {
  const kind = SABERES[f.id].kind;
  const who = SPEAKER[kind] ?? 'brann';
  return h(
    'div.loot-reveal.saber-reveal',
    { 'data-test': 'saber-reveal', 'data-saber': f.id },
    h('span.lr-rays'),
    h('span.sr-flame', { 'aria-hidden': 'true' }),
    label ? h('span.lr-label', {}, t('saber.found')) : '',
    h(
      'div.sr-body',
      {},
      h('span.sr-ico', { html: KIND_ICON[kind] ?? ICONS.scroll }),
      h(
        'div',
        {},
        h('span.sr-kind', {}, t(`saber.kind.${kind}`)),
        h('b.sr-name', {}, t(`saber.${f.id}`)),
        h('p.sr-lore', {}, t(`saber.${f.id}.lore`)),
      ),
    ),
    h('blockquote.sr-npc', { 'data-npc': who }, h('span.sr-who', {}, who === 'brann' ? 'Brann' : 'Hilde'), t(`saber.${f.id}.npc`)),
    h('p.sr-use', {}, t(`saber.use.${kind}`)),
    h('p.lr-where', {}, saberBy(s, me, f)),
  );
};

/** The saberes found since the bastion knew `before` of them. */
export const sabersSince = (s: CampaignState, before: number): SaberFind[] => (s.saberes?.known ?? []).slice(before);

/* ---------- seen, per player and campaign, in the browser ---------- */

const seenKey = (s: CampaignState, me: string): string => `somnhold.saberes.seen.${s.id}.${me}`;

/** How many of the bastion's saberes this player has been shown (0 if the browser forgot). */
const seenCount = (s: CampaignState, me: string): number => {
  try {
    return Number(localStorage.getItem(seenKey(s, me)) ?? 0) || 0;
  } catch {
    return 0;
  }
};

/** This player has now seen every saber the bastion knows. */
export const markSabersSeen = (s: CampaignState, me: string): void => {
  try {
    localStorage.setItem(seenKey(s, me), String(s.saberes?.known.length ?? 0));
  } catch {
    // Private window or storage blocked: the reveal shows again next time, which is harmless.
  }
};

/**
 * The saberes this player hasn't been shown: found while away (a wave, the Chasm's weekly chest) or by a friend.
 * On a new browser all of them count as unseen: they show once, together, which is a fine way to meet them.
 */
export const unseenSabers = (s: CampaignState, me: string): SaberFind[] => {
  const known = s.saberes?.known ?? [];
  return known.slice(Math.min(seenCount(s, me), known.length));
};

/* ---------- the bastion's list ---------- */

/** The saberes the bastion knows, newest first, each with who brought it; or the line that says none yet. */
export const sabersList = (s: CampaignState, me: string): HTMLElement => {
  const known = [...(s.saberes?.known ?? [])].reverse();
  // The panel's own title already says «Saberes»: the hint only.
  const box = h('section.sabers', { 'data-test': 'sabers' }, h('p.small.hint', {}, t('saber.hint')));
  if (!known.length) {
    box.append(h('p.sabers-none', {}, t('saber.none')));
    return box;
  }
  const list = h('ul.sabers-list');
  for (const f of known) {
    const kind = SABERES[f.id].kind;
    list.append(
      h(
        'li.saber-row',
        { 'data-saber': f.id },
        h('span.sr-ico', { html: KIND_ICON[kind] ?? ICONS.scroll }),
        h('div', {}, h('div.saber-head', {}, h('b', {}, saberTitle(s, f.id)), h('span.sr-kind', {}, t(`saber.kind.${kind}`))), h('p.small', {}, t(`saber.${f.id}.lore`)), h('p.small.hint', {}, saberBy(s, me, f))),
      ),
    );
  }
  box.append(list);
  return box;
};

/** A saber's name; the cat with its own once it has one, and its kittens. */
export const saberTitle = (s: CampaignState, id: SaberId): string => {
  if (id !== 'cat' || s.saberes?.catName === undefined) return t(`saber.${id}`);
  const n = kittensOf(s);
  const name = t('cat.named', { n: t(`cat.name.${s.saberes.catName}`) });
  return n ? `${name} ${t(n === 1 ? 'cat.kittens.one' : 'cat.kittens.many', { n })}` : name;
};

/** The cat's kittens: one more with each era past the first (docs/SABERES.md, «Decisiones»), up to CAT.kittens. */
export const kittensOf = (s: CampaignState): number => Math.min(CAT.kittens, Math.max(0, eraOf(s) - 1));

/* ---------- the rite of the Tide, at the keep ---------- */

/**
 * The rites the bastion knows (docs/SABERES.md §4.3): one for the coming Marea, chosen in the calm before it, undone
 * only by who chose it until the general starts drafting. Nothing shows before a rite is known (rule 4).
 */
export const ritesBox = (s: CampaignState, me: string, cmd: (c: CampaignCommand) => Promise<boolean>, closed = false): HTMLElement | '' => {
  const known = RITE_IDS.filter((id) => s.saberes?.known.some((k) => k.id === id));
  if (!known.length) return '';
  const box = h('section.rites', { 'data-test': 'rites' }, h('h4', {}, t('rite.title')), h('p.small.hint', {}, t('rite.hint')));
  // Each rite's numbers: the lullaby's fewer and tougher, the alarm's more and what it pays back.
  const desc = (id: (typeof RITE_IDS)[number]): string =>
    t(`rite.${id}.desc`, id === 'alarm' ? { a: Math.round(RITES.alarm.budgetBp / 100) - 100, b: Math.round(ALARM.payBp / 100) } : { a: 100 - Math.round(RITES[id].budgetBp / 100), b: Math.round(RITES[id].strengthBp / 100) - 100 });
  const chosen = riteFor(s, s.mareaIndex);
  // Closed by the calendar too (`closed`: the general starts drafting the first wave, riteClosesAt).
  const open = riteOpen(s) && !closed;
  const dust = offeringCost(s);
  const m = s.mareaIndex + 1;
  if (chosen && s.rite) {
    const mine = s.rite.by === me;
    // What is on, said in full for the whole Marea, not only its name.
    box.append(h('div.rite.on', { 'data-test': 'rite-chosen' }, h('b', {}, t(`saber.${chosen}`)), h('span.small', {}, desc(chosen)), h('span.small.hint', {}, t(mine ? 'rite.chosenYou' : 'rite.chosen', { m, r: t(`saber.${chosen}`), who: s.players.find((p) => p.id === s.rite?.by)?.displayName ?? '—' }))));
    if (mine && open) box.append(h('button.btn.small.ghost', { 'data-act': 'rite-undo', onclick: () => void cmd({ t: 'riteUndo' }) }, t('rite.undo')));
    else if (!open) box.append(h('p.small.hint', {}, t('rite.closed')));
    return box;
  }
  if (!open) {
    box.append(h('p.small.hint', {}, t('rite.wait')));
    return box;
  }
  const row = h('div.rite-row');
  // The alarm's bet is no bet with a boss coming (the server says so too).
  const boss = Array.from({ length: WAVES_PER_MAREA }, (_, w) => bossAt(s.mareaIndex, w)).some(Boolean);
  for (const id of known) {
    const noBet = id === 'alarm' && boss;
    row.append(
      h(
        'button.rite',
        { 'data-rite': id, disabled: s.storage.dust < dust || noBet, title: noBet ? t('reason.alarmBoss') : '', onclick: () => void cmd({ t: 'rite', id }) },
        h('b', {}, t(`saber.${id}`)),
        h('span.small', {}, desc(id)),
        h('span.offer-cost', {}, t('rite.do'), ' ', h('span.cost-chip', {}, h('span', { html: ICONS.dust }), String(dust))),
      ),
    );
  }
  box.append(row);
  return box;
};

/* ---------- the group's ensign and the stone of names (§4.5) ---------- */

/**
 * The ensign's editor: a field, a colour and a sign, with a preview; raised once an era by anyone, no vote. `svg`
 * draws a preview (the render's own, so the editor shows what hangs from the gate).
 */
export const ensignBox = (s: CampaignState, me: string, cmd: (c: CampaignCommand) => Promise<boolean>, svg: (field: number, color: number, sign: number) => string, rerender: () => void): HTMLElement | '' => {
  if (!s.saberes?.known.some((k) => k.id === 'ensign')) return '';
  const cur = s.saberes.ensign;
  const box = h('section.ensign', { 'data-test': 'ensign' }, h('div.sec-title', {}, h('h4', {}, t('ensign.title'))), h('p.small.hint', {}, t('ensign.hint')));
  const locked = !!cur && cur.era === eraOf(s);
  const pick = { field: cur?.field ?? 0, color: cur?.color ?? 0, sign: cur?.sign ?? 0 };
  const preview = h('div.ensign-preview', { html: svg(pick.field, pick.color, pick.sign), 'aria-hidden': 'true' });
  box.append(preview);
  const eraName = ['I', 'II', 'III', 'IV', 'V'][(cur?.era ?? 1) - 1] ?? String(cur?.era ?? 1);
  if (cur) box.append(h('p.small.hint', {}, cur.by === me ? t('ensign.setByYou', { era: eraName }) : t('ensign.setBy', { who: nameOf(s, me, cur.by), era: eraName })));
  if (locked) {
    box.append(h('p.small', {}, t('ensign.locked')));
    return box;
  }
  const row = (key: 'field' | 'color' | 'sign', n: number): HTMLElement => {
    const r = h('div.ensign-row');
    for (let i = 0; i < n; i++)
      r.append(
        h(`button.btn.small.ghost.ensign-pick${pick[key] === i ? '.on' : ''}`, {
          'aria-pressed': String(pick[key] === i),
          'data-ensign': `${key}-${i}`,
          onclick: (ev: Event) => {
            pick[key] = i;
            preview.innerHTML = svg(pick.field, pick.color, pick.sign);
            for (const b of r.querySelectorAll('.ensign-pick')) b.classList.toggle('on', b === ev.currentTarget);
          },
        }, t(`ensign.${key}.${i}`)),
      );
    return r;
  };
  // Each row under its own label, so a phone doesn't leave one stranded.
  for (const [key, n] of [['field', ENSIGN.fields], ['color', ENSIGN.colors], ['sign', ENSIGN.signs]] as const) box.append(h('span.small.hint.ensign-label', {}, t(`ensign.${key}`)), row(key, n));
  box.append(h('button.btn', { 'data-act': 'ensign-save', onclick: async () => { if (await cmd({ t: 'ensign', ...pick })) rerender(); } }, t('ensign.save')));
  return box;
};

/** The stone of names: Marea by Marea, who fought in it, newest first. */
export const namestoneBox = (s: CampaignState, me: string): HTMLElement | '' => {
  if (!s.saberes?.known.some((k) => k.id === 'namestone')) return '';
  const lines = [...(s.saberes.names ?? [])].reverse();
  const box = h('section.namestone', { 'data-test': 'namestone' }, h('div.sec-title', {}, h('h4', {}, t('namestone.title'))));
  if (!lines.length) {
    box.append(h('p.small.hint', {}, t('namestone.empty')));
    return box;
  }
  const list = h('ul.namestone-lines');
  for (const l of lines)
    list.append(h('li', {}, h('b', {}, t('namestone.line', { m: l.marea + 1 })), h('span.small', {}, ` · ${dateOf(l.at)} · `), h('span', {}, listNames(l.pids.map((pid) => nameOf(s, me, pid))))));
  box.append(list);
  return box;
};
